import { z } from "zod"

import { DECIMALS_PRECISION } from "@/constants/formats"
import { WarehouseType } from "@/graphql/codegen/graphql"
import { hasMaxDecimalPlaces } from "@/utils/forms"

export const warehouseLocationFormSchema = z.object({
  companyId: z.string().uuid("Company is mandatory"),
  locationId: z.string().uuid("Address is mandatory"),
  externalSystemId: z
    .string()
    .min(1, "AS400 Warehouse ID is required")
    .refine((value) => !value || /^\d{2}$/.test(value), {
      message: "AS400 Warehouse ID must be a 2-digit numeric value",
    }),
})

export const warehouseMiscellaneousFeeFormSchema = z
  .object({
    type: z.string(),
    amount: z.string().refine((value) => !value || hasMaxDecimalPlaces(value, DECIMALS_PRECISION), {
      message: `Charge amount value must be a valid currency with up to ${DECIMALS_PRECISION} decimal places`,
    }),
  })
  .superRefine((fee, ctx) => {
    if (!fee.amount && fee.type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "You must enter charge amount value",
        path: ["amount"],
      })
    }

    if (!fee.type && fee.amount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "You must enter charge type value",
        path: ["type"],
      })
    }
  })

export const warehouseInfoFormSchema = z.object({
  type: z.nativeEnum(WarehouseType),
  receivingHoursFrom: z.string().nullable(),
  receivingHoursTo: z.string().nullable(),
  shippingHoursFrom: z.string().nullable(),
  shippingHoursTo: z.string().nullable(),
  specialInstructions: z.string(),
  monthlyAccrualFee: z.string().refine((value) => !value || hasMaxDecimalPlaces(value, DECIMALS_PRECISION), {
    message: `Monthly accrual fee value must be a valid currency with up to ${DECIMALS_PRECISION} decimal places`,
  }),
  inboundFee: z.string().refine((value) => !value || hasMaxDecimalPlaces(value, DECIMALS_PRECISION), {
    message: `Warehouse inbound fee value must be a valid currency with up to ${DECIMALS_PRECISION} decimal places`,
  }),
  outboundFee: z.string().refine((value) => !value || hasMaxDecimalPlaces(value, DECIMALS_PRECISION), {
    message: `Warehouse outbound fee value must be a valid currency with up to ${DECIMALS_PRECISION} decimal places`,
  }),
  miscellaneousFees: z.array(warehouseMiscellaneousFeeFormSchema),
})

export const warehouseFormSchema = warehouseLocationFormSchema.merge(warehouseInfoFormSchema)

export type WarehouseLocationFormValues = z.infer<typeof warehouseLocationFormSchema>

export type WarehouseMiscellaneousFeeFormValues = z.infer<typeof warehouseMiscellaneousFeeFormSchema>

export type WarehouseInfoFormValues = z.infer<typeof warehouseInfoFormSchema>

export type WarehouseFormValues = z.infer<typeof warehouseFormSchema>
